/**
 * RolesManagement - Page de gestion des rôles et permissions
 * Système RBAC avancé avec permissions granulaires
 * Inclut: Assignation en masse, Audit Log, Export/Import
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { RequirePermission } from '../../components/permissions/RequirePermission';
import { PERMS } from '../../utils/permissions';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Shield, ShieldCheck, Plus, Edit, Copy, Trash2, Users,
  Package, FileText, MessageSquare, CreditCard, BarChart2,
  Settings, AlertTriangle, Truck, Navigation, Lock,
  Search, Check, X, ChevronRight, Info, Download, Upload,
  History, UserCheck, RefreshCw, Filter, Calendar, Sparkles, Eye, ShieldAlert
} from 'lucide-react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import BulkRoleAssignment from '../../components/rbac/BulkRoleAssignment';
import AuditLog from '../../components/rbac/AuditLog';
import ExportImport from '../../components/rbac/ExportImport';
import PermissionEditor from '../../components/rbac/PermissionEditor';
import PacksManagement from '../../components/rbac/PacksManagement';
import RoleCreationWizard from '../../components/rbac/RoleCreationWizard';
import RbacCoverageAudit from '../../components/rbac/RbacCoverageAudit';
import DataScopeManager from '../../components/rbac/DataScopeManager';
import UserPermissionReport from '../../components/rbac/UserPermissionReport';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icônes par module (pour l'affichage des cartes)
const MODULE_ICONS = {
  operations: Package,
  quotes: FileText,
  users: Users,
  messaging: MessageSquare,
  finances: CreditCard,
  reports: BarChart2,
  settings: Settings,
  roles: Shield,
  incidents: AlertTriangle,
  fleet: Truck,
  tracking: Navigation,
};

const RolesManagement = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [permissionCategories, setPermissionCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCloneDialog, setShowCloneDialog] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [packs, setPacks] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#6366f1',
    permissions: []
  });
  const [cloneName, setCloneName] = useState('');
  const [saving, setSaving] = useState(false);

  // Fetch data
  useEffect(() => {
    fetchRoles();
    fetchPermissions();
    fetchPacks();
  }, []);

  // Deep-link : si on arrive avec ?fromPack=<slug>, ouvrir le wizard automatiquement
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('fromPack')) {
      setShowWizard(true);
    }
  }, [searchParams]);

  const fetchPacks = async () => {
    try {
      const response = await axios.get(`${API}/rbac/packs`);
      setPacks(response.data || []);
    } catch (e) {
      console.warn('Failed to fetch packs:', e?.response?.data || e.message);
    }
  };

  const fetchRoles = async () => {
    try {
      // Cache-buster + header explicite : garantit que la vue n'affiche jamais
      // une réponse HTTP mise en cache par le navigateur / proxy (bug perms stale UI).
      const response = await axios.get(`${API}/rbac/roles`, {
        params: { _ts: Date.now() },
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      });
      setRoles(response.data);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger les rôles",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Refresh auto quand l'onglet reprend le focus (utile après import / re-seed
  // dans un autre onglet ou après une modif par un autre admin).
  useEffect(() => {
    const onFocus = () => fetchRoles();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const fetchPermissions = async () => {
    try {
      const response = await axios.get(`${API}/rbac/permissions/categories`);
      setPermissionCategories(response.data);
      
      // Flatten permissions
      const allPerms = response.data.flatMap(cat => cat.permissions);
      setPermissions(allPerms);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  // Handlers
  const handleCreateRole = async () => {
    if (!formData.name.trim()) {
      toast({ title: "Erreur", description: "Le nom est requis", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/rbac/roles`, formData);
      setRoles([...roles, response.data]);
      setShowCreateDialog(false);
      resetForm();
      toast({ title: "Succès", description: "Rôle créé avec succès" });
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de la création",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedRole) return;

    setSaving(true);
    try {
      await axios.put(`${API}/rbac/roles/${selectedRole.id}`, {
        name: formData.name,
        description: formData.description,
        color: formData.color,
        permissions: formData.permissions
      });

      // Bug fix perms stale UI : on RE-FETCH depuis le serveur au lieu de patcher
      // l'état local avec response.data (qui pouvait masquer des divergences DB
      // ex : perm non reconnue, doublons filtrés, count désynchro).
      await fetchRoles();
      setShowEditDialog(false);
      setSelectedRole(null);
      resetForm();
      toast({ title: "Succès", description: "Rôle mis à jour avec succès" });
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de la mise à jour",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloneRole = async () => {
    if (!selectedRole || !cloneName.trim()) return;

    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/rbac/roles/${selectedRole.id}/clone?new_name=${encodeURIComponent(cloneName)}`
      );
      
      setRoles([...roles, response.data]);
      setShowCloneDialog(false);
      setSelectedRole(null);
      setCloneName('');
      toast({ title: "Succès", description: "Rôle cloné avec succès" });
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors du clonage",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!selectedRole) return;

    setSaving(true);
    try {
      await axios.delete(`${API}/rbac/roles/${selectedRole.id}`);
      setRoles(roles.filter(r => r.id !== selectedRole.id));
      setShowDeleteDialog(false);
      setSelectedRole(null);
      toast({ title: "Succès", description: "Rôle supprimé avec succès" });
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de la suppression",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      color: '#6366f1',
      permissions: []
    });
  };

  const openEditDialog = (role) => {
    setSelectedRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      color: role.color,
      permissions: role.permissions || []
    });
    setShowEditDialog(true);
  };

  const openCloneDialog = (role) => {
    setSelectedRole(role);
    setCloneName(`${role.name} (copie)`);
    setShowCloneDialog(true);
  };

  const openDeleteDialog = (role) => {
    setSelectedRole(role);
    setShowDeleteDialog(true);
  };

  const togglePermission = (permId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permId)
        ? prev.permissions.filter(p => p !== permId)
        : [...prev.permissions, permId]
    }));
  };

  const toggleCategoryPermissions = (categoryId, permissions) => {
    const catPermIds = permissions.map(p => p.id);
    const allSelected = catPermIds.every(id => formData.permissions.includes(id));
    
    setFormData(prev => ({
      ...prev,
      permissions: allSelected
        ? prev.permissions.filter(p => !catPermIds.includes(p))
        : [...new Set([...prev.permissions, ...catPermIds])]
    }));
  };

  // Filter roles
  const filteredRoles = roles.filter(role => 
    role.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    role.slug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // RBAC v2 — Re-seeder système (utile après déploiement fresh ou ajout de nouvelles perms)
  const [seedingRbac, setSeedingRbac] = useState(false);
  const handleReseedRbac = async () => {
    if (!window.confirm(
      'Cette action va recréer/mettre à jour les 10 rôles système et 10 packs prédéfinis avec les dernières permissions.\n\n' +
      'Vos rôles custom ne seront PAS touchés. Continuer ?'
    )) return;
    setSeedingRbac(true);
    try {
      const { data } = await axios.post(`${API}/rbac/init`);
      toast({
        title: '✅ RBAC réinitialisé',
        description: `${data.totalRoles} rôles · ${data.totalPacks} packs · ${data.totalPermissions} permissions`,
      });
      await fetchRoles();
      await fetchPacks();
    } catch (e) {
      toast({
        title: 'Erreur',
        description: e.response?.data?.detail || 'Impossible de réinitialiser le RBAC',
        variant: 'destructive',
      });
    } finally {
      setSeedingRbac(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg md:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-emerald-600" />
            Gestion des Rôles
          </h1>
          <p className="text-gray-500 mt-1">
            Créez et gérez les rôles et permissions des utilisateurs
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleReseedRbac}
            disabled={seedingRbac}
            data-testid="rbac-reseed-btn"
            title="Réinitialise les 10 rôles système et 10 packs prédéfinis avec les dernières permissions du catalogue. Utile après un déploiement fresh ou l'ajout de nouvelles permissions. Aucun rôle custom n'est affecté."
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${seedingRbac ? 'animate-spin' : ''}`} />
            {seedingRbac ? 'Réinitialisation…' : 'Re-seeder système'}
          </Button>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs defaultValue="roles" className="w-full">
        <TabsList className="grid w-full grid-cols-8 mb-6">
          <TabsTrigger value="roles" className="flex items-center gap-2" data-testid="rbac-tab-roles">
            <Shield className="w-4 h-4" />
            Rôles
          </TabsTrigger>
          <TabsTrigger value="packs" className="flex items-center gap-2" data-testid="rbac-tab-packs">
            <Package className="w-4 h-4" />
            Packs
          </TabsTrigger>
          <TabsTrigger value="assign" className="flex items-center gap-2" data-testid="rbac-tab-assign">
            <UserCheck className="w-4 h-4" />
            Assignation
          </TabsTrigger>
          <TabsTrigger value="report" className="flex items-center gap-2" data-testid="rbac-tab-report">
            <ShieldAlert className="w-4 h-4" />
            Rapport
          </TabsTrigger>
          <TabsTrigger value="audit" className="flex items-center gap-2" data-testid="rbac-tab-audit">
            <History className="w-4 h-4" />
            Journal
          </TabsTrigger>
          <TabsTrigger value="coverage" className="flex items-center gap-2" data-testid="rbac-tab-coverage">
            <BarChart2 className="w-4 h-4" />
            Couverture
          </TabsTrigger>
          <TabsTrigger value="scoping" className="flex items-center gap-2" data-testid="rbac-tab-scoping">
            <Eye className="w-4 h-4" />
            Data-scoping
          </TabsTrigger>
          <TabsTrigger value="export" className="flex items-center gap-2" data-testid="rbac-tab-export">
            <Download className="w-4 h-4" />
            Export/Import
          </TabsTrigger>
        </TabsList>

        {/* Tab: Roles Management */}
        <TabsContent value="roles" className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="relative max-w-md flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher un rôle..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button
              onClick={async () => { setLoading(true); await fetchRoles(); toast({ title: 'Rôles actualisés', description: 'Comptages resynchronisés depuis la BDD.' }); }}
              variant="outline"
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
              data-testid="refresh-roles-btn"
              title="Recharge la liste des rôles et permissions depuis la base (ignore le cache navigateur)."
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            <Button
              onClick={() => setShowWizard(true)}
              variant="outline"
              className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
              data-testid="open-wizard-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Assistant guidé
            </Button>
            <Button
              onClick={() => {
                resetForm();
                setShowCreateDialog(true);
              }}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau rôle
            </Button>
          </div>

          {/* Roles Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredRoles.map(role => (
              <Card 
                key={role.id} 
                className={`hover:shadow-md transition-shadow ${
                  role.isSystem ? 'border-l-4' : ''
                }`}
                style={{ borderLeftColor: role.isSystem ? role.color : undefined }}
              >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${role.color}20` }}
                  >
                    <Shield className="w-5 h-5" style={{ color: role.color }} />
                  </div>
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      {role.name}
                      {role.isSystem && (
                        <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                          <Lock className="w-3 h-3 mr-1" />
                          Système
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {role.slug}
                    </CardDescription>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {role.description && (
                <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                  {role.description}
                </p>
              )}
              
              <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                <span className="flex items-center gap-1">
                  <Shield className="w-4 h-4" />
                  {role.permissions?.length || 0} permissions
                </span>
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {role.userCount || 0} utilisateurs
                </span>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEditDialog(role)}
                  className="flex-1"
                >
                  <Edit className="w-3 h-3 mr-1" />
                  Modifier
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openCloneDialog(role)}
                  title="Cloner ce rôle"
                >
                  <Copy className="w-3 h-3" />
                </Button>
                {!role.isSystem && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openDeleteDialog(role)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    title="Supprimer"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
        </TabsContent>

        {/* Tab: Packs de permissions (V2) */}
        <TabsContent value="packs">
          <PacksManagement
            allPermissions={permissions}
            allRoles={roles}
            onPackApplied={fetchRoles}
          />
        </TabsContent>

        {/* Tab: Bulk Assignment */}
        <TabsContent value="assign">
          <BulkRoleAssignment roles={roles} onRefresh={fetchRoles} />
        </TabsContent>

        {/* Tab: Audit Log */}
        <TabsContent value="audit">
          <AuditLog />
        </TabsContent>

        {/* Tab: Rapport permissions utilisateur */}
        <TabsContent value="report">
          <UserPermissionReport />
        </TabsContent>

        {/* Tab: Couverture RBAC (durcissement endpoints) */}
        <TabsContent value="coverage">
          <RbacCoverageAudit />
        </TabsContent>

        <TabsContent value="scoping">
          <DataScopeManager />
        </TabsContent>

        {/* Tab: Export/Import */}
        <TabsContent value="export">
          <ExportImport onRefresh={fetchRoles} />
        </TabsContent>
      </Tabs>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Créer un nouveau rôle</DialogTitle>
            <DialogDescription>
              Définissez les informations et les permissions du nouveau rôle
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="info" className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="info">Informations</TabsTrigger>
              <TabsTrigger value="permissions">
                Permissions ({formData.permissions.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="info" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom du rôle *</Label>
                  <Input
                    placeholder="Ex: Gestionnaire Opérations"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Couleur</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="w-12 h-9 p-1 cursor-pointer"
                    />
                    <Input
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Décrivez le rôle et ses responsabilités..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>
            </TabsContent>

            <TabsContent value="permissions" className="mt-4">
              <PermissionEditor
                permissionCategories={permissionCategories}
                selectedPermissions={formData.permissions}
                onToggle={togglePermission}
                onToggleCategory={toggleCategoryPermissions}
              />
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleCreateRole} 
              disabled={saving || !formData.name.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {saving ? 'Création...' : 'Créer le rôle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Modifier le rôle
              {selectedRole?.isSystem && (
                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700">
                  <Lock className="w-3 h-3 mr-1" />
                  Rôle système
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedRole?.isSystem && (
                <span className="text-amber-600 flex items-center gap-1 mt-1">
                  <Info className="w-4 h-4" />
                  Ce rôle système peut être modifié mais il est recommandé de le cloner pour créer une variante.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="info" className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="info">Informations</TabsTrigger>
              <TabsTrigger value="permissions">
                Permissions ({formData.permissions.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="info" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom du rôle *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Couleur</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="w-12 h-9 p-1 cursor-pointer"
                    />
                    <Input
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>
            </TabsContent>

            <TabsContent value="permissions" className="mt-4">
              <PermissionEditor
                permissionCategories={permissionCategories}
                selectedPermissions={formData.permissions}
                onToggle={togglePermission}
                onToggleCategory={toggleCategoryPermissions}
              />
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleUpdateRole} 
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clone Dialog */}
      <Dialog open={showCloneDialog} onOpenChange={setShowCloneDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cloner le rôle</DialogTitle>
            <DialogDescription>
              Créer un nouveau rôle basé sur &quot;{selectedRole?.name}&quot; avec toutes ses permissions.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nom du nouveau rôle *</Label>
              <Input
                value={cloneName}
                onChange={(e) => setCloneName(e.target.value)}
                placeholder="Ex: Commercial Senior"
              />
            </div>
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <p className="flex items-center gap-2">
                <Info className="w-4 h-4" />
                Le nouveau rôle héritera de {selectedRole?.permissions?.length || 0} permissions.
                Vous pourrez les modifier après la création.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloneDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleCloneRole} 
              disabled={saving || !cloneName.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {saving ? 'Clonage...' : 'Cloner le rôle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le rôle ?</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer le rôle &quot;{selectedRole?.name}&quot; ?
              {selectedRole?.userCount > 0 && (
                <span className="block mt-2 text-red-600 font-medium">
                  ⚠️ Ce rôle est utilisé par {selectedRole.userCount} utilisateur(s).
                  Veuillez d&apos;abord leur assigner un autre rôle.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRole}
              className="bg-red-600 hover:bg-red-700"
              disabled={saving || (selectedRole?.userCount > 0)}
            >
              {saving ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* V2.3 — Wizard de création de rôle guidé */}
      <RoleCreationWizard
        open={showWizard}
        onClose={() => {
          setShowWizard(false);
          // Nettoie le query param pour éviter une réouverture en boucle
          if (searchParams.get('fromPack')) {
            const sp = new URLSearchParams(searchParams);
            sp.delete('fromPack');
            setSearchParams(sp, { replace: true });
          }
        }}
        allPacks={packs}
        allPermissions={permissions}
        initialPackSlug={searchParams.get('fromPack') || null}
        onCreated={() => {
          fetchRoles();
        }}
      />
    </div>
  );
};

export default function RolesManagementGuarded(props) {
  return (
    <RequirePermission
      anyPermission={[PERMS.ROLES_VIEW, PERMS.ROLES_EDIT, PERMS.ROLES_CREATE]}
    >
      <RolesManagement {...props} />
    </RequirePermission>
  );
}
